import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import type { AdController } from "../ads/types";

export function AdRail(): React.JSX.Element | null {
  const host = useRef<HTMLElement>(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const config = window.PicPlayerAdsConfig;
  const enabled = config?.enabled === true;

  useEffect(() => {
    if (!enabled || !host.current) return;
    const element = host.current;
    let controller: AdController | undefined;
    const mount = (): void => {
      if (controller || !window.PicPlayerAds) return;
      try {
        controller = window.PicPlayerAds.mount({ ...config, element, appId: "modelmotive", minViewportWidth: 1280,
          privacyUrl: new URL("./privacy.html", document.baseURI).href,
          cssUrl: new URL("./ads/rail.css", document.baseURI).href });
      } catch { element.dataset.adState = "unavailable"; }
    };
    const choices = (event: Event): void => {
      // A CMP can intercept this same cancelable event before it reaches document.
      if (event.defaultPrevented) return;
      event.preventDefault();
      setRevoked(false);
      setPreferencesOpen(true);
    };
    mount();
    window.addEventListener("load", mount, { once: true });
    document.addEventListener("picplayer:ad-preferences", choices);
    return () => {
      controller?.destroy();
      window.removeEventListener("load", mount);
      document.removeEventListener("picplayer:ad-preferences", choices);
    };
  }, [enabled, config]);

  if (!enabled) return null;
  return <>
    <aside ref={host} className="shared-ad-rail" data-ad-app="modelmotive" aria-label="Sponsored advertisements">
      <div className="ad-sdk-fallback"><span>SPONSORED</span><h3>A little space.<br />More possibility.</h3><p>Advertising supports these creative tools. Your models stay on your device.</p><a href="./privacy.html" target="_blank" rel="noopener noreferrer">Privacy &amp; advertising</a></div>
    </aside>
    <Dialog.Root open={preferencesOpen} onOpenChange={setPreferencesOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content ad-preferences-dialog" aria-describedby="ad-preferences-description">
          <div className="dialog-header"><div><Dialog.Title>Advertising preferences</Dialog.Title><Dialog.Description id="ad-preferences-description">Your models are not advertising data.</Dialog.Description></div><Dialog.Close className="icon-button" aria-label="Close advertising preferences">×</Dialog.Close></div>
          <div className="ad-preferences-body">
            <p>ModelMotive uses the shared PicPlayer ad manager. Requests identify this app and the ad placement, not your model, its name, geometry, files or canvas.</p>
            <p>The default is contextual text advertising. Third-party networks can run only after a connected consent platform authorizes the specific network. This preferences panel does not grant that authorization.</p>
            <p>You can withdraw advertising consent here. Contextual text ads or a local fallback may remain.</p>
            <button type="button" className="button" onClick={() => { window.PicPlayerAds?.setConsent({ ads: false, networkIds: [] }); window.dispatchEvent(new CustomEvent("modelmotive:ad-consent-withdrawn")); setRevoked(true); }}>Use contextual ads only</button>
            {revoked && <p role="status" className="consent-status">Advertising consent withdrawn for this session. Your consent platform should also save this preference.</p>}
            <a href="./privacy.html" target="_blank" rel="noopener noreferrer">Read the privacy &amp; advertising notice</a>
          </div>
          <div className="dialog-footer"><Dialog.Close className="button primary">Done</Dialog.Close></div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </>;
}
